import { ChartInsight } from '@api';
import { PriceDisplay, YAxisType } from '@chart/types/axis.types';
import {
  BaseChartAPIProps,
  CreateChartOptions,
  CreateChartOptionsWithColors,
  CreateChartOptionsWithConfig,
} from '@chart/types/create.types';
import { SeriesType } from '@chart/types/series.types';
import { ChartAssetData } from '@chart/types/timeseries.types';
import { COLLAPSED_PANE_HEIGHT, PANE_MAX_COUNT } from '@chart/utils/constants';
import { createCanvasContext } from '@chart/utils/createCanvasContext';
import { differenceInMilliseconds } from 'date-fns';

import {
  drawLiveCrosshair,
  getNextBoundaryUTC,
} from '../crosshair/drawLiveCrosshair';
import { dispatchInsightsInDomainEvent } from '../events/events';
import { reDraw } from './redraw/redraw';
import { InitProps, setup } from './setup/setup';

interface ChartAPIProps {
  canvasElement: HTMLCanvasElement;
  options: CreateChartOptions;
}

export interface AddPaneProps {
  data: ChartAssetData[];
  priceDisplay: PriceDisplay;
  yAxisType: YAxisType;
  seriesType: SeriesType;
}

export interface ChartAPIReturn {
  init: (props: InitProps) => void;
  displayInsights: (insights: ChartInsight[]) => void;
  getBaseData: () => BaseChartAPIProps | undefined;
  removeListeners: () => void;
  reset: () => void;
  updateLastPrice: (
    price: number,
    timestamp?: string,
    entityIdentifier?: string
  ) => void;
}

const DEFAULT_CONFIG: CreateChartOptionsWithConfig['config'] = {
  enableZoom: true,
  enableHover: true,
  paneMaxCount: PANE_MAX_COUNT,
  collapsedPaneHeight: COLLAPSED_PANE_HEIGHT,
  hoverVariant: 'default',
  panePaddingTop: 0,
  panePaddingRight: 0,
};

export const chartAPI = ({
  canvasElement,
  options,
}: ChartAPIProps): ChartAPIReturn | null => {
  const context = createCanvasContext(canvasElement, options);
  let rafId: number | undefined;
  let lastCountdownLabel: string | undefined;

  if (!context) {
    return null;
  }

  const optionsWithConfig: CreateChartOptionsWithColors = {
    ...options,
    config: {
      ...DEFAULT_CONFIG,
      ...options.config,
    },
    colors: options.colors,
  };
  const chartSetup = setup(canvasElement, optionsWithConfig, context);

  const startLiveCountdown = () => {
    if (rafId) {
      return false;
    }

    const loop = () => {
      const base = chartSetup.chartStore.getState().base;

      if (!base) {
        stopLiveCountdown();
        return;
      }

      if (base.resampleInterval !== '1m') {
        stopLiveCountdown();
        return;
      }

      const last = base.primaryAsset.ts.at(-1);
      const nextBoundary =
        last && getNextBoundaryUTC(new Date(last.time), base.resampleInterval);
      const remainingMs = nextBoundary
        ? Math.max(0, differenceInMilliseconds(nextBoundary, new Date()))
        : 0;
      const secs = Math.max(0, Math.ceil(remainingMs / 1000));
      const label = `0:${secs.toString().padStart(2, '0')}`;

      if (label !== lastCountdownLabel) {
        lastCountdownLabel = label;
        reDraw({ chartStore: chartSetup.chartStore });
        drawLiveCrosshair(base);
      }

      rafId = window.requestAnimationFrame(loop);
    };

    rafId = window.requestAnimationFrame(loop);
    return true;
  };

  const stopLiveCountdown = () => {
    if (rafId) {
      window.cancelAnimationFrame(rafId);
      rafId = undefined;
      lastCountdownLabel = undefined;
    }
  };

  let countdownStarted = false;

  return {
    init: props => {
      chartSetup.initialiseData(props);
      chartSetup.setListeners(true);
    },
    displayInsights: insights => {
      const newBase = chartSetup.chartStore.getState().setInsights(insights);
      dispatchInsightsInDomainEvent(newBase);
    },
    getBaseData: () => {
      return chartSetup.chartStore.getState().base;
    },
    removeListeners: () => {
      chartSetup.setListeners(false);
      stopLiveCountdown();
    },
    reset: () => {
      chartSetup.setListeners(false);
      chartSetup.chartStore.setState({ base: undefined });
      stopLiveCountdown();
    },
    updateLastPrice: (price, timestamp, entityIdentifier) => {
      if (!countdownStarted) {
        countdownStarted = startLiveCountdown();
      }

      chartSetup.chartStore
        .getState()
        .updateLastPrice(price, timestamp, entityIdentifier);
      reDraw({ chartStore: chartSetup.chartStore });

      const base = chartSetup.chartStore.getState().base;

      if (base) {
        drawLiveCrosshair(base);
      }
    },
  };
};
